<script setup>
/* Dialog "Spielerliste exportieren": Umfang, Format, Exportieren.
 *
 * Wird nur für admin, board, data und staff eingehängt (AccountMenu.vue, SelectionBar.vue); die eigentliche Prüfung
 * steht zusätzlich in usePlayerListExport.run. Die Datei entsteht im Browser (siehe dort).
 *
 * Prop `selection`: aus der Aktionsleiste des Markier-Modus geöffnet. Dann ist der Umfang fest "selection"
 * (die markierten Spieler), die Umfangsliste entfällt und es gibt das Feld "List title". Titel und Format
 * werden für das nächste Öffnen gemerkt (nur im Speicher), weil man oft zwei Formate hintereinander braucht.
 */

import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import OverlayModal from './OverlayModal.vue';
import { usePlayerListExport } from '../composables/usePlayerListExport.js';
import { useSnackbar } from '../composables/useSnackbar.js';
import {
  EXPORT_FORMATS, DEFAULT_SCOPE, DEFAULT_FORMAT, SELECTION_SCOPE, LIST_TITLE_MAX,
} from '../domain/playerListExport.js';

/* Gemerkt zwischen zwei Öffnungen aus der Aktionsleiste */
const remembered = { title: '', format: DEFAULT_FORMAT };

const open = defineModel({ type: Boolean, default: false });
const props = defineProps({
  selection: { type: Boolean, default: false },
});

const { t } = useI18n();
const { busy, error, countFor, run, scopes } = usePlayerListExport();

/* Nur die Umfänge, die die Rolle exportieren darf (staff: Kader). Hat sie nur einen, steht er als fester Text. */
const listedScopes = computed(() => scopes.value.filter((s) => s !== SELECTION_SCOPE));
const fixedScope = computed(() => (listedScopes.value.length === 1 ? listedScopes.value[0] : null));
const { show } = useSnackbar();

const scope = ref(DEFAULT_SCOPE);
const format = ref(DEFAULT_FORMAT);
const title = ref('');

/* Beim Öffnen auf die Vorgaben zurück — und die alte Fehlermeldung weg */
watch(open, (isOpen) => {
  if (isOpen) {
    scope.value = props.selection ? SELECTION_SCOPE : DEFAULT_SCOPE;
    format.value = props.selection ? remembered.format : DEFAULT_FORMAT;
    title.value = props.selection ? remembered.title : '';
    error.value = null;
  }
}, { immediate: true });

function close() {
  if (!busy.value) open.value = false;
}

async function submit() {
  const result = await run({ scope: scope.value, format: format.value, title: title.value });
  if (result.ok) {
    if (props.selection) {
      remembered.title = title.value;
      remembered.format = format.value;
    }
    show(t(props.selection ? 'selection.listCreated' : 'playerExport.done'));
    open.value = false;
  }
}
</script>

<template>
  <OverlayModal v-if="open" :title="$t('playerExport.title')" :width="460" @close="close">
    <p class="pe-intro">{{ $t('playerExport.intro') }}</p>

    <div v-if="!selection && fixedScope" class="pe-group pe-fixed" data-testid="pe-scope-fixed">
      <span class="pe-legend">{{ $t('playerExport.scopeLabel') }}</span>
      <b>{{ $t(`playerExport.scope.${fixedScope}`) }}</b>
      <span class="pe-hint">{{ $t(`playerExport.scopeHint.${fixedScope}`) }}</span>
    </div>

    <fieldset v-else-if="!selection" class="pe-group" data-testid="pe-scope">
      <legend>{{ $t('playerExport.scopeLabel') }}</legend>
      <label v-for="s in listedScopes" :key="s" class="pe-option" :class="{ on: scope === s }">
        <input v-model="scope" type="radio" name="pe-scope" :value="s" :disabled="busy" />
        <span>
          <b>{{ $t(`playerExport.scope.${s}`) }}</b>
          <span class="pe-hint">{{ $t(`playerExport.scopeHint.${s}`) }}</span>
        </span>
      </label>
      <p class="pe-note">{{ $t('playerExport.releasedNote') }}</p>
    </fieldset>

    <label v-if="selection" class="pe-group pe-title" data-testid="pe-title">
      <span class="pe-legend">{{ $t('selection.listTitle') }}</span>
      <input
        v-model="title"
        type="text"
        class="pe-input"
        :maxlength="LIST_TITLE_MAX"
        :placeholder="$t('selection.listTitlePlaceholder')"
        :disabled="busy"
        autocomplete="off"
      />
    </label>

    <fieldset class="pe-group" data-testid="pe-format">
      <legend>{{ $t('playerExport.formatLabel') }}</legend>
      <div class="pe-formats">
        <label v-for="f in EXPORT_FORMATS" :key="f" class="pe-option pe-format" :class="{ on: format === f }">
          <input v-model="format" type="radio" name="pe-format" :value="f" :disabled="busy" />
          <b>{{ $t(`playerExport.format.${f}`) }}</b>
        </label>
      </div>
    </fieldset>

    <!-- Leere Auswahl: klarer Hinweis, der Exportknopf bleibt gesperrt (keine leere Datei) -->
    <p v-if="!countFor(scope)" class="pe-count" data-testid="pe-empty">{{ $t(`playerExport.emptyScope.${scope}`) }}</p>
    <p v-else class="pe-count">{{ $t('playerExport.count', { count: countFor(scope) }) }}</p>
    <p v-if="error" class="pe-error" role="alert">{{ $t(`playerExport.${error}`) }}</p>

    <template #actions>
      <button class="btn ghost" :disabled="busy" @click="close">{{ $t('common.cancel') }}</button>
      <button class="btn" data-testid="pe-submit" :disabled="busy || !countFor(scope)" @click="submit">
        {{ busy ? $t('playerExport.working') : $t('playerExport.submit') }}
      </button>
    </template>
  </OverlayModal>
</template>

<style scoped>
.pe-intro {
  margin: 0 0 14px;
  color: var(--muted);
  font-size: 0.85rem;
  line-height: 1.5;
}
.pe-group {
  margin: 0 0 14px;
  padding: 0;
  border: 0;
  min-width: 0;
}
.pe-group legend {
  padding: 0;
  margin-bottom: 6px;
  font-size: 0.72rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--dim);
}
.pe-title {
  display: block;
}
.pe-legend {
  display: block;
  margin-bottom: 6px;
  font-size: 0.72rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--dim);
}
.pe-input {
  width: 100%;
  box-sizing: border-box;
  min-height: 40px;
  padding: 0 12px;
  font: inherit;
  font-size: 13px;
  color: var(--text);
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 9px;
}
.pe-option {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  padding: 9px 11px;
  margin-bottom: 6px;
  border: 1px solid var(--border);
  border-radius: 9px;
  font-size: 0.85rem;
  color: var(--text);
  cursor: pointer;
  transition: border-color 0.14s, background 0.14s;
}
.pe-option.on {
  border-color: var(--green-dim);
  background: var(--green-bg);
}
.pe-option input {
  margin-top: 3px;
  accent-color: var(--green);
}
.pe-hint {
  display: block;
  margin-top: 3px;
  font-size: 0.78rem;
  color: var(--muted);
}
.pe-note {
  margin: 2px 0 0;
  font-size: 0.78rem;
  color: var(--dim);
}
.pe-formats {
  display: flex;
  gap: 8px;
}
.pe-format {
  flex: 1;
  align-items: center;
  margin-bottom: 0;
}
.pe-format input {
  margin-top: 0;
}
.pe-count {
  margin: 0;
  font-size: 0.85rem;
  color: var(--text);
}
.pe-error {
  margin: 10px 0 0;
  font-size: 0.82rem;
  color: var(--red);
}
</style>
